'use client';

import { c as _c } from "react/compiler-runtime";
import { HeaderItem } from '@7f-ui/components/header-item';
import { CartCountDocument } from '@data-access/lib/gql/cart/documents';
import { getGraphqlQueryKey, useGraphQL } from '@data-access/lib/graphql-query';
import { MerchantCTA } from '@feat/feat-search/merchant-cta';
import { HeartStraightIcon, ToteIcon, UserIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { shopTestId } from '@utils/utils-shop-test-ids';
import Link from 'next/link';
import { useEffect } from 'react';
import type { HeaderConfig, HeaderLabels, HeaderViewer } from './header.types';
type HeaderNavProps = {
  cartCount: number;
  pathname: string;
  config: HeaderConfig;
  labels: HeaderLabels;
  viewer: HeaderViewer;
  wishlistExists: boolean;
  onOpenLoginModal: () => void;
};
export function HeaderNav(t0) {
  const $ = _c(36);
  const {
    cartCount,
    pathname,
    config,
    labels,
    viewer,
    wishlistExists,
    onOpenLoginModal
  } = t0;
  const profileLabel = viewer?.firstName ?? labels.profile;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== cartCount) {
    t1 = {
      initialData: {
        cartCount
      },
      refetchOnMount: false,
      refetchOnWindowFocus: true,
      select: _temp
    };
    $[0] = cartCount;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: t2
  } = useGraphQL(CartCountDocument, undefined, t1);
  const liveCartCount = t2 === undefined ? cartCount : t2;
  let t3;
  let t4;
  if ($[2] !== cartCount || $[3] !== queryClient) {
    t3 = () => {
      queryClient.setQueryData(getGraphqlQueryKey(CartCountDocument), {
        cartCount
      });
    };
    t4 = [cartCount, queryClient];
    $[2] = cartCount;
    $[3] = queryClient;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== config) {
    t5 = config.isHomePage && <MerchantCTA config={config} />;
    $[6] = config;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = viewer ? "/profile/wishlists/" : "/guest/wishlists/";
  let t7;
  if ($[8] !== pathname) {
    t7 = pathname.startsWith("/profile/wishlists") || pathname.startsWith("/guest/wishlists");
    $[8] = pathname;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = shopTestId("Header.wishlist");
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = !config.isHomePageScrolledToTop;
  let t10;
  if ($[11] !== labels.wishlist || $[12] !== t6 || $[13] !== t7 || $[14] !== t9 || $[15] !== wishlistExists) {
    t10 = <HeaderItem as={Link} href={t6} active={t7} circle={wishlistExists} data-testid={t8} label={labels.wishlist} Icon={HeartStraightIcon} onDarkBg={t9} />;
    $[11] = labels.wishlist;
    $[12] = t6;
    $[13] = t7;
    $[14] = t9;
    $[15] = wishlistExists;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== pathname) {
    t11 = pathname.startsWith("/cart");
    $[17] = pathname;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = shopTestId("Header.cart");
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = !config.isHomePageScrolledToTop;
  let t14;
  if ($[20] !== labels.cart || $[21] !== liveCartCount || $[22] !== t11 || $[23] !== t13) {
    t14 = <HeaderItem as={Link} href="/cart/" active={t11} count={liveCartCount} data-testid={t12} label={labels.cart} Icon={ToteIcon} onDarkBg={t13} />;
    $[20] = labels.cart;
    $[21] = liveCartCount;
    $[22] = t11;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== config.isHomePageScrolledToTop || $[26] !== onOpenLoginModal || $[27] !== pathname || $[28] !== profileLabel || $[29] !== viewer) {
    t15 = viewer ? <HeaderItem as={Link} truncate={true} href="/profile/" checked={true} active={pathname.startsWith("/profile") && !pathname.startsWith("/profile/wishlists")} data-testid={shopTestId("Header.profile")} label={profileLabel} Icon={UserIcon} onDarkBg={!config.isHomePageScrolledToTop} /> : <HeaderItem onClick={onOpenLoginModal} data-testid={shopTestId("Header.profile")} label={profileLabel} Icon={UserIcon} onDarkBg={!config.isHomePageScrolledToTop} />;
    $[25] = config.isHomePageScrolledToTop;
    $[26] = onOpenLoginModal;
    $[27] = pathname;
    $[28] = profileLabel;
    $[29] = viewer;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t10 || $[32] !== t14 || $[33] !== t15 || $[34] !== t5) {
    t16 = <nav className="flex items-center gap-1 sm:gap-2 md:gap-4 lg:gap-6">{t5}{t10}{t14}{t15}</nav>;
    $[31] = t10;
    $[32] = t14;
    $[33] = t15;
    $[34] = t5;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
}
function _temp(data) {
  return data.cartCount;
}